/**
 * TopBar - Barre supérieure pour ServUp
 *
 * Affiche le titre de la page (+ bouton hamburger sur mobile/tablet),
 * et delegue les 3 actions a droite (etablissement / user / deconnexion)
 * au composant TopBarUserActions en variant 'topbar'.
 *
 * Sur phone (< 768px) : la TopBar n'affiche QUE [hamburger] [titre].
 * Les 3 actions sont rendues dans le drawer (cf MainLayout.tsx).
 */

import React from 'react';
import { SavUser } from '../../SupabaseWrapper';
import { useViewport } from '../../hooks/useViewport';
import TopBarUserActions from './TopBarUserActions';

const HamburgerIcon: React.FC<{ size?: number; color?: string }> = ({ size = 22, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

interface TopBarProps {
  pageTitle: string;
  pageSubtitle?: string;
  user: SavUser | null;
  onLogout: () => void;
  onOpenDrawer?: () => void;  // si fourni, affiche le bouton hamburger qui ouvre le drawer
}

function TopBar({ pageTitle, pageSubtitle, user, onLogout, onOpenDrawer }: TopBarProps) {
  const { isPhone } = useViewport();

  return (
    <header style={{
      backgroundColor: 'white',
      borderBottom: '1px solid #e2e8f0',
      padding: isPhone ? '12px' : '16px 24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'relative',
      zIndex: 50
    }}>
      {/* Bouton hamburger (mobile/tablet uniquement) + Titre de la page */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
        {onOpenDrawer && (
          <button
            onClick={onOpenDrawer}
            aria-label="Ouvrir le menu"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 8,
              border: 'none',
              backgroundColor: 'transparent',
              color: '#475569',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <HamburgerIcon size={22} />
          </button>
        )}
        <div style={{ minWidth: 0 }}>
          <h1 style={{
            fontSize: isPhone ? '18px' : '20px',
            fontWeight: '600',
            color: '#1e293b',
            margin: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {pageTitle}
          </h1>
          {pageSubtitle && (
            <p style={{
              fontSize: '13px',
              color: '#64748b',
              margin: '4px 0 0 0',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {pageSubtitle}
            </p>
          )}
        </div>
      </div>

      {/* Actions a droite : sur phone, elles sont rendues dans le drawer (cf MainLayout) */}
      {!isPhone && (
        <TopBarUserActions user={user} onLogout={onLogout} variant="topbar" />
      )}
    </header>
  );
}

export default TopBar;
